import { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [leads, setLeads] = useState(100);
  const [closeRate, setCloseRate] = useState(20);
  const [dealValue, setDealValue] = useState(1500);
  const [improvement, setImprovement] = useState(30);
  const [ghlCost, setGhlCost] = useState(297);
  const [email, setEmail] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef(null);

  const currentRevenue = leads * (closeRate / 100) * dealValue;
  const projectedLeads = leads * (1 + improvement / 100);
  const projectedRevenue = projectedLeads * (closeRate / 100) * dealValue;
  const monthlyIncrease = projectedRevenue - currentRevenue;
  const monthlyNetProfit = monthlyIncrease - ghlCost;
  const annualNetProfit = monthlyNetProfit * 12;
  const roi = ghlCost > 0 ? ((monthlyIncrease - ghlCost) / ghlCost) * 100 : 0;

  const maxBar = Math.max(currentRevenue, projectedRevenue) || 1;
  const currentBarWidth = (currentRevenue / maxBar) * 100;
  const projectedBarWidth = (projectedRevenue / maxBar) * 100;

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  const formatChange = (value) =>
    `${value >= 0 ? '+' : ''}${formatCurrency(value)}`;

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!email) return;
    setUnlocked(true);
    setTimeout(() => setShowResults(true), 50);
  };

  useEffect(() => {
    if (showResults && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showResults]);

  return (
    <div className="app">
      <div className="calculator">
        <div className="header">
          <span className="eyebrow">GoHighLevel ROI Calculator</span>
          <h1>See What Automation Is Worth</h1>
          <p className="subtitle">
            Estimate the revenue GoHighLevel automation can add to your business
          </p>
        </div>

        <div className="inputs-section">
          <div className="input-group fade-in" style={{ animationDelay: '0.1s' }}>
            <label htmlFor="leads">Current Leads Per Month</label>
            <input
              id="leads"
              type="number"
              min="0"
              placeholder="e.g. 100"
              value={leads}
              onChange={(e) => setLeads(Math.max(0, Number(e.target.value)))}
            />
            <span className="helper">Total inbound leads from all sources</span>
          </div>

          <div className="input-group fade-in" style={{ animationDelay: '0.2s' }}>
            <label htmlFor="closeRate">Current Close Rate (%)</label>
            <div className="input-with-suffix">
              <input
                id="closeRate"
                type="number"
                min="0"
                max="100"
                placeholder="e.g. 20"
                value={closeRate}
                onChange={(e) => setCloseRate(Math.min(100, Math.max(0, Number(e.target.value))))}
              />
              <span className="suffix">%</span>
            </div>
            <span className="helper">Percentage of leads that become paying clients</span>
          </div>

          <div className="input-group fade-in" style={{ animationDelay: '0.3s' }}>
            <label htmlFor="dealValue">Average Deal Value ($)</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                id="dealValue"
                type="number"
                min="0"
                placeholder="e.g. 1500"
                value={dealValue}
                onChange={(e) => setDealValue(Math.max(0, Number(e.target.value)))}
              />
            </div>
            <span className="helper">Revenue per closed deal or contract</span>
          </div>

          <div className="input-group fade-in" style={{ animationDelay: '0.4s' }}>
            <label htmlFor="improvement">
              Expected Lead Increase with Automation (%)
              <span className="tooltip" title="Businesses using GHL typically see 20–50% more leads through automated follow-ups, speed-to-lead, and nurture campaigns. 30% is a conservative starting point.">?</span>
            </label>
            <div className="input-with-suffix">
              <input
                id="improvement"
                type="number"
                min="0"
                max="500"
                placeholder="e.g. 30"
                value={improvement}
                onChange={(e) => setImprovement(Math.min(500, Math.max(0, Number(e.target.value))))}
              />
              <span className="suffix">%</span>
            </div>
            <span className="benchmark">Most GHL users see 20–50% lift. 30% is conservative.</span>
          </div>

          <div className="input-group fade-in" style={{ animationDelay: '0.5s' }}>
            <label htmlFor="ghlCost">
              Monthly GHL Cost ($)
              <span className="tooltip" title="Your monthly GoHighLevel subscription cost. Starter is ~$97/mo, Unlimited is ~$297/mo, SaaS Pro is ~$497/mo.">?</span>
            </label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                id="ghlCost"
                type="number"
                min="0"
                placeholder="e.g. 297"
                value={ghlCost}
                onChange={(e) => setGhlCost(Math.max(0, Number(e.target.value)))}
              />
            </div>
          </div>
        </div>

        {!unlocked ? (
          <form className="email-gate fade-in" onSubmit={handleUnlock}>
            <p className="gate-text">Enter your email to see your projected results</p>
            <div className="gate-row">
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="email-input"
              />
              <button type="submit" className="unlock-button">Show My Results</button>
            </div>
          </form>
        ) : (
          <div ref={resultsRef} className={`results-wrapper ${showResults ? 'results-visible' : ''}`}>
            <div className="chart-section">
              <h2>Revenue Comparison</h2>
              <div className="chart">
                <div className="chart-row">
                  <span className="chart-label">Current</span>
                  <div className="bar-track">
                    <div
                      className="bar bar-current"
                      style={{ width: showResults ? `${currentBarWidth}%` : '0%' }}
                    />
                  </div>
                  <span className="chart-value">{formatCurrency(currentRevenue)}</span>
                </div>
                <div className="chart-row">
                  <span className="chart-label">With GHL</span>
                  <div className="bar-track">
                    <div
                      className="bar bar-projected"
                      style={{ width: showResults ? `${projectedBarWidth}%` : '0%' }}
                    />
                  </div>
                  <span className="chart-value chart-value-highlight">{formatCurrency(projectedRevenue)}</span>
                </div>
              </div>
            </div>

            <div className="results-section">
              <h2>Your Projected Results</h2>
              <div className="results-grid">
                <div className="result-card pop-in" style={{ animationDelay: '0.2s' }}>
                  <span className="result-label">Monthly Revenue Increase</span>
                  <span className={`result-value ${monthlyIncrease >= 0 ? 'increase' : 'decrease'}`}>{formatChange(monthlyIncrease)}</span>
                </div>
                <div className="result-card highlight pop-in" style={{ animationDelay: '0.35s' }}>
                  <span className="result-label">Monthly GHL Cost</span>
                  <span className="result-value">{formatCurrency(ghlCost)}</span>
                </div>
                <div className="result-card accent pop-in" style={{ animationDelay: '0.5s' }}>
                  <span className="result-label">Monthly Net Profit</span>
                  <span className={`result-value ${monthlyNetProfit >= 0 ? 'increase' : 'decrease'}`}>{formatChange(monthlyNetProfit)}</span>
                </div>
                <div className="result-card accent pop-in" style={{ animationDelay: '0.65s' }}>
                  <span className="result-label">Annual Net Profit</span>
                  <span className={`result-value ${annualNetProfit >= 0 ? 'increase' : 'decrease'}`}>{formatChange(annualNetProfit)}</span>
                </div>
              </div>

              <div className="roi-badge pop-in" style={{ animationDelay: '0.8s' }}>
                <span className="roi-label">Return on Investment</span>
                <span className={`roi-value ${roi >= 0 ? 'increase' : 'decrease'}`}>{roi >= 0 ? '+' : ''}{Math.round(roi)}% ROI</span>
              </div>
            </div>

            <a href="https://calendar.app.google/JHhe6yPMPQURi63T9" target="_blank" rel="noopener noreferrer" className="cta-button pop-in" style={{ animationDelay: '0.95s' }}>
              Book a Free GHL Strategy Call
            </a>

            <p className="disclaimer">
              *These projections are estimates based on the inputs you provide and are not guaranteed results. Actual outcomes may vary depending on your industry, market conditions, and implementation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
